import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';

import ContinuityCard from '@/components/student-dashboard/ContinuityCard';
import ErrorMessage from '@/components/shared/ErrorMessage';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Divider, Skeleton } from '@/components/ui/daisy';
import { useStudentDashboard } from '@/data/context/StudentDashboardContext';
import useLessonNameFormatter from '@/data/hook/lesson/useLessonNameFormatter';
import useStudentContinuity from '@/data/hook/student-dashboard/useStudentContinuity';
import useStudentPendencies from '@/data/hook/student-dashboard/useStudentPendencies';
import StudentPendenciesSection from '@/pages/dashboard/student/StudentPendenciesSection';
import { getBannerImg } from '@/utils/coursePath';

const MOBILE_PENDENCIES_LIMIT = 1;
const DESKTOP_PENDENCIES_LIMIT = 2;
const PENDENCIES_GRID_COLS = 'md:grid-cols-[minmax(0,1fr)_minmax(16rem,20rem)]';

export default function StudentContinuitySection() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'studentDashboard.continuity',
  });
  const { activeCourseProgress, isLoading: isLoadingContext } =
    useStudentDashboard();
  const lessonNameFormatter = useLessonNameFormatter();

  const courseProgressId = activeCourseProgress?.id;
  const klassId = activeCourseProgress?.klassId;
  const slug = activeCourseProgress?.coursePath.slug;

  const {
    lesson,
    unitProgress,
    href,
    isLoading: isLoadingContinuity,
    error: continuityError,
  } = useStudentContinuity({ courseProgressId, klassId, slug });

  const {
    examPendency,
    homeworkCount,
    nextLessonDate,
    hasPendencies,
    isLoading: isLoadingPendencies,
    error: pendenciesError,
  } = useStudentPendencies({
    studentId: activeCourseProgress?.studentId,
    klassId,
    courseProgressId,
  });

  const isLoading =
    isLoadingContext || isLoadingContinuity || isLoadingPendencies;

  if (isLoading) return <LoadingView />;

  if (continuityError)
    return <ErrorMessage text={t('loadError')} className="h-24" />;

  if (!activeCourseProgress || !lesson) return null;

  const showPendencies = hasPendencies || !!pendenciesError;

  return (
    <section
      className={twJoin('grid gap-6', showPendencies && PENDENCIES_GRID_COLS)}
    >
      <ContinuityCard
        lesson={{
          ...lesson,
          bannerImg:
            lesson.bannerImg || getBannerImg(activeCourseProgress.coursePath),
        }}
        progress={unitProgress}
        lessonNameFormatter={lessonNameFormatter}
        href={href}
      >
        <ConditionalRenderer condition={showPendencies}>
          <div className="flex flex-col gap-4 md:hidden">
            <Divider className="my-0 h-px" />
            <StudentPendenciesSection
              exam={examPendency}
              homeworkCount={homeworkCount}
              nextLessonDate={nextLessonDate}
              limit={MOBILE_PENDENCIES_LIMIT}
              error={pendenciesError}
            />
          </div>
        </ConditionalRenderer>
      </ContinuityCard>
      <ConditionalRenderer condition={showPendencies}>
        <div className="hidden min-w-0 md:flex md:flex-col">
          <StudentPendenciesSection
            exam={examPendency}
            homeworkCount={homeworkCount}
            nextLessonDate={nextLessonDate}
            limit={DESKTOP_PENDENCIES_LIMIT}
            error={pendenciesError}
            className="flex-1"
          />
        </div>
      </ConditionalRenderer>
    </section>
  );
}

function LoadingView() {
  return (
    <section className={twJoin('grid gap-6', PENDENCIES_GRID_COLS)}>
      <Skeleton className="h-56 w-full rounded-2xl" />
      <div className="hidden gap-3 md:flex md:flex-col">
        <Skeleton className="flex-1 rounded-2xl" />
        <Skeleton className="flex-1 rounded-2xl" />
      </div>
    </section>
  );
}
